<template>
  <div class="login-container">
    <div class="logo-container">
      <img src="@/assets/img/logo.png" alt="LubConsulta" class="logo">
    </div>

    <div class="login-card">
      <h2 class="login-title">Acesse sua conta</h2>
      <p class="login-subtitle">Informe suas credenciais para acessar a plataforma</p>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label class="form-label">Usuário ou E-mail</label>
          <input 
            type="text" 
            class="form-input" 
            v-model="credentials.username"
            placeholder="Digite seu usuário ou e-mail"
            required
          >
        </div>

        <div class="form-group">
          <label class="form-label">Senha</label>
          <div class="password-input-container">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              class="form-input password-input" 
              v-model="credentials.password"
              placeholder="Digite sua senha"
              required
            >
            <button 
              type="button" 
              class="password-toggle"
              @click="togglePasswordVisibility"
            >
              <i :class="showPassword ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
            </button>
          </div>
        </div>

        <div class="form-options">
          <label class="remember-me">
            <input type="checkbox" v-model="rememberMe">
            <span class="checkmark"></span>
            Lembrar-me
          </label>
          
          <a href="#" @click.prevent="openForgotPasswordModal" class="forgot-password">
            Esqueceu a senha?
          </a>
          <a href="#" @click.prevent="openResetPasswordModal" class="reset-password">
            Resetar senha?
          </a>
        </div>

        <button 
          type="submit" 
          class="login-button" 
          :disabled="isLoading"
        >
          <span v-if="!isLoading">Entrar</span>
          <div v-else class="loading-spinner"></div>
        </button>
      </form>
    </div>

    <!-- Modal de Alerta -->
    <div v-if="showAlert" class="alert-modal" :class="alertAnimation">
      <div class="alert-content">
        <div class="alert-icon">
          <i class="ti ti-alert-circle"></i>
        </div>
        <div class="alert-message">
          <h3>Alerta de Menssagem</h3>
          <p>{{ alertMessage }}</p>
        </div>
        <button class="alert-close" @click="closeAlert">
          <i class="ti ti-x"></i>
        </button>
      </div>
    </div>

    <!-- Modal Esqueceu a Senha -->
    <div v-if="showForgotPasswordModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">Recuperar Senha</h2>
          <button class="modal-close" @click="closeForgotPasswordModal">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-subtitle">Informe seu e-mail para receber as informações da conta</p>
          
          <form @submit.prevent="handleForgotPassword" class="modal-form">
            <div class="form-group">
              <label class="form-label">E-mail</label>
              <input 
                type="email" 
                class="form-input" 
                v-model="forgotPasswordEmail"
                placeholder="Digite seu e-mail cadastrado"
                required
              >
            </div>
            
            <div class="modal-actions">
              <button 
                type="button" 
                class="modal-button secondary" 
                @click="closeForgotPasswordModal"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                class="modal-button primary" 
                :disabled="isLoadingForgotPassword"
              >
                <span v-if="!isLoadingForgotPassword">Enviar</span>
                <div v-else class="loading-spinner small"></div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Resetar Senha -->
    <div v-if="showResetPasswordModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">Resetar Senha</h2>
          <button class="modal-close" @click="closeResetPasswordModal">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-subtitle">Informe seus dados para resetar a senha</p>
          
          <form @submit.prevent="handleResetPassword" class="modal-form">
            <div class="form-group">
              <label class="form-label">Usuário ou E-mail</label>
              <input 
                type="text" 
                class="form-input" 
                v-model="resetPasswordData.username"
                placeholder="Digite seu usuário ou e-mail"
                required
              >
            </div>

            <div class="form-group">
              <label class="form-label">E-mail</label>
              <input 
                type="email" 
                class="form-input" 
                v-model="resetPasswordData.email"
                placeholder="Digite seu e-mail"
                required
              >
            </div>

            <div class="form-group">
              <label class="form-label">Senha Atual</label>
              <div class="password-input-container">
                <input 
                  :type="showCurrentPassword ? 'text' : 'password'" 
                  class="form-input password-input" 
                  v-model="resetPasswordData.currentPassword"
                  placeholder="Digite sua senha atual"
                  required
                >
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  <i :class="showCurrentPassword ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                </button>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Nova Senha</label>
              <div class="password-input-container">
                <input 
                  :type="showNewPassword ? 'text' : 'password'" 
                  class="form-input password-input" 
                  v-model="resetPasswordData.newPassword"
                  placeholder="Digite a nova senha"
                  required
                >
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                </button>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Confirmar Nova Senha</label>
              <div class="password-input-container">
                <input 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  class="form-input password-input" 
                  v-model="resetPasswordData.confirmPassword"
                  placeholder="Confirme a nova senha"
                  required
                >
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                </button>
              </div>
            </div>

            <div class="modal-actions">
              <button 
                type="button" 
                class="modal-button secondary" 
                @click="closeResetPasswordModal"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                class="modal-button primary" 
                :disabled="isLoadingResetPassword"
              >
                <span v-if="!isLoadingResetPassword">Resetar Senha</span>
                <div v-else class="loading-spinner small"></div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Botão WhatsApp -->
    <div class="whatsapp-button" @click="toggleWhatsAppChat">
      <div class="pulse">
        <i class="ti ti-brand-whatsapp"></i>
      </div>
    </div>

    <!-- Chat WhatsApp -->
    <div v-if="showWhatsAppChat" class="whatsapp-chat">
      <div class="chat-header">
        <h3>Envie sua mensagem</h3>
        <button @click="toggleWhatsAppChat" class="close-chat">
          <i class="ti ti-x"></i>
        </button>
      </div>
      <div class="chat-body">
        <textarea v-model="whatsappMessage" placeholder="Digite sua mensagem..."></textarea>
      </div>
      <div class="chat-footer">
        <button @click="sendWhatsAppMessage" class="send-button">
          Enviar <i class="ti ti-send"></i>
        </button>
      </div>
    </div>

    <footer class="site-footer">
      @ lubconsuta.com.br
    </footer>
  </div>
</template>

<script>
import apiClient from '@/services/api.js';

export default {
  name: 'LoginView',
  data() {
    return {
      credentials: {
        username: '',
        password: ''
      },
      showPassword: false,
      rememberMe: false,
      isLoading: false,
      showAlert: false,
      alertMessage: '',
      alertAnimation: '',
      showWhatsAppChat: false,
      whatsappMessage: '',
      
      // Modais
      showForgotPasswordModal: false,
      showResetPasswordModal: false,
      
      // Dados do modal Esqueceu a Senha
      forgotPasswordEmail: '',
      isLoadingForgotPassword: false,
      
      // Dados do modal Resetar Senha
      resetPasswordData: {
        username: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      isLoadingResetPassword: false
    }
  },
  methods: {
    async handleLogin() {
      this.isLoading = true;

      try {
        if (!this.credentials.username || !this.credentials.password) {
          this.showError('Por favor, preencha todos os campos.');
          return;
        }

        const response = await apiClient.post('/cognito/login', {
          username: this.credentials.username,
          password: this.credentials.password
        });

        if (response.data.success) {
          if (this.rememberMe) {
            localStorage.setItem('userToken', response.data.token);
            localStorage.setItem('userData', JSON.stringify(response.data.user));
          } else {
            sessionStorage.setItem('userToken', response.data.token);
            sessionStorage.setItem('userData', JSON.stringify(response.data.user));
          }

          this.$router.push('/pagedefault');
        } else {
          this.showError('Usuário ou senha incorretos.');
        }

      } catch (error) {
        console.error('Erro no login:', error);
        this.showError('Usuário ou senha incorretos.');
      } finally {
        this.isLoading = false;
      }
    },

    // Métodos para Esqueceu a Senha
    openForgotPasswordModal() {
      this.showForgotPasswordModal = true;
      this.forgotPasswordEmail = '';
    },

    closeForgotPasswordModal() {
      this.showForgotPasswordModal = false;
      this.forgotPasswordEmail = '';
      this.isLoadingForgotPassword = false;
    },

    // No método handleForgotPassword do Vue.js
    async handleForgotPassword() {
        if (!this.forgotPasswordEmail) {
            this.showError('Por favor, informe seu e-mail.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.forgotPasswordEmail)) {
            this.showError('Por favor, informe um e-mail válido.');
            return;
        }

        this.isLoadingForgotPassword = true;

        try {
            // Chamada única para o endpoint que já busca e envia e-mail
            const response = await apiClient.post('/cognito/password/info', {
                email: this.forgotPasswordEmail
            });

            if (response.data.success) {
                this.showSuccess('Informações enviadas para seu e-mail com sucesso!');
                this.closeForgotPasswordModal();
            } else {
                this.showError(response.data.message || 'Erro ao processar sua solicitação.');
            }
        } catch (error) {
            console.error('Erro ao recuperar senha:', error);
            if (error.response && error.response.status === 404) {
                this.showError('E-mail não encontrado em nossa base de dados.');
            } else {
                this.showError('Erro ao processar sua solicitação. Tente novamente.');
            }
        } finally {
            this.isLoadingForgotPassword = false;
        }
    },

    async sendUserInfoEmail(userInfo) {
      try {
        // Esta função enviaria o e-mail com as informações do usuário
        // Como não tenho acesso direto ao backend de e-mail, esta é uma implementação simulada
        console.log('Enviando e-mail com informações do usuário:', userInfo);
        // Em um ambiente real, você faria uma chamada API para enviar o e-mail
      } catch (error) {
        console.error('Erro ao enviar e-mail:', error);
        throw error;
      }
    },

    // Métodos para Resetar Senha
    openResetPasswordModal() {
      this.showResetPasswordModal = true;
      this.resetPasswordData = {
        username: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
    },

    closeResetPasswordModal() {
      this.showResetPasswordModal = false;
      this.resetPasswordData = {
        username: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.isLoadingResetPassword = false;
    },

    async handleResetPassword() {
      // Validações
      if (!this.resetPasswordData.username || 
          !this.resetPasswordData.email || 
          !this.resetPasswordData.currentPassword || 
          !this.resetPasswordData.newPassword || 
          !this.resetPasswordData.confirmPassword) {
        this.showError('Por favor, preencha todos os campos.');
        return;
      }

      if (this.resetPasswordData.newPassword !== this.resetPasswordData.confirmPassword) {
        this.showError('A nova senha e a confirmação não coincidem.');
        return;
      }

      this.isLoadingResetPassword = true;

      try {
        const response = await apiClient.post('/cognito/password/reset', this.resetPasswordData);

        if (response.data.success) {
          this.showSuccess('Senha alterada com sucesso!');
          this.closeResetPasswordModal();
          // Redirecionar para login após 2 segundos
          setTimeout(() => {
            this.$router.push('/login');
          }, 2000);
        } else {
          this.showError('Não foi possível resetar a senha.');
        }
      } catch (error) {
        console.error('Erro ao resetar senha:', error);
        this.showError('Erro ao resetar senha. Verifique os dados informados.');
      } finally {
        this.isLoadingResetPassword = false;
      }
    },

    // Métodos auxiliares
    showError(message) {
      this.alertMessage = message;
      this.showAlert = true;
      
      const animations = ['slide-top', 'slide-right', 'slide-left'];
      const randomAnimation = animations[Math.floor(Math.random() * animations.length)];
      this.alertAnimation = randomAnimation;

      setTimeout(() => {
        this.closeAlert();
      }, 5000);
    },

    showSuccess(message) {
      this.alertMessage = message;
      this.showAlert = true;
      
      // Alterar estilo para sucesso
      const alertContent = document.querySelector('.alert-content');
      if (alertContent) {
        alertContent.style.borderLeftColor = '#28a745';
      }
      
      const alertIcon = document.querySelector('.alert-icon');
      if (alertIcon) {
        alertIcon.style.color = '#28a745';
      }

      const animations = ['slide-top', 'slide-right', 'slide-left'];
      const randomAnimation = animations[Math.floor(Math.random() * animations.length)];
      this.alertAnimation = randomAnimation;

      setTimeout(() => {
        this.closeAlert();
      }, 5000);
    },

    closeAlert() {
      this.showAlert = false;
      this.alertAnimation = '';
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    toggleWhatsAppChat() {
      this.showWhatsAppChat = !this.showWhatsAppChat;
    },

    sendWhatsAppMessage() {
      if (this.whatsappMessage.trim()) {
        const phoneNumber = '5585992282002';
        const encodedMessage = encodeURIComponent(this.whatsappMessage);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
        
        window.open(whatsappUrl, '_blank');
        this.whatsappMessage = '';
        this.showWhatsAppChat = false;
      }
    }
  },
  mounted() {
    const savedUsername = localStorage.getItem('savedUsername');
    if (savedUsername) {
      this.credentials.username = savedUsername;
      this.rememberMe = true;
    }
  }
}
</script>

<style scoped>
.login-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  position: relative;
  min-height: 100vh;
  padding-bottom: 60px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.logo-container {
  text-align: center;
  margin-bottom: 40px;
}

.logo {
  max-width: 350px;
  height: auto;
}

.login-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
}

.login-title {
  color: #333;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 10px;
  text-align: center;
}

.login-subtitle {
  color: #666;
  text-align: center;
  margin-bottom: 30px;
  font-size: 14px;
}

.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 38px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #E4A64E;
  box-shadow: 0 0 0 2px rgba(228, 166, 78, 0.1);
}

.password-input-container {
  position: relative;
}

.password-input {
  padding-right: 50px;
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
}

.password-toggle:hover {
  color: #E4A64E;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  font-size: 14px;
}

.remember-me {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #666;
}

.remember-me input {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-radius: 3px;
  margin-right: 8px;
  position: relative;
  transition: all 0.3s ease;
}

.remember-me input:checked + .checkmark {
  background-color: #E4A64E;
  border-color: #E4A64E;
}

.remember-me input:checked + .checkmark::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 12px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.forgot-password {
  color: #E4A64E;
  text-decoration: none;
  font-weight: 500;
}

.forgot-password:hover {
  text-decoration: underline;
}

.reset-password {
  color: #E4A64E;
  text-decoration: none;
  font-weight: 500;
}

.reset-password:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  padding: 15px;
  background-color: #E4A64E;
  color: white;
  border: none;
  border-radius: 38px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
}

.login-button:hover:not(:disabled) {
  background-color: #d6963e;
}

.login-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.login-footer {
  margin-top: 25px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.register-link {
  color: #E4A64E;
  text-decoration: none;
  font-weight: 500;
}

.register-link:hover {
  text-decoration: underline;
}

/* Modal de Alerta */
.alert-modal {
  position: fixed;
  top: 20px;
  z-index: 1000;
  width: 100%;
  max-width: 400px;
  left: 50%;
  transform: translateX(-50%);
}

.alert-modal.slide-top {
  animation: slideTop 0.5s ease-out;
}

.alert-modal.slide-right {
  animation: slideRight 0.5s ease-out;
}

.alert-modal.slide-left {
  animation: slideLeft 0.5s ease-out;
}

.alert-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: flex-start;
  border-left: 4px solid #dc3545;
}

.alert-icon {
  color: #dc3545;
  font-size: 24px;
  margin-right: 15px;
}

.alert-message h3 {
  margin: 0 0 5px 0;
  color: #dc3545;
  font-size: 16px;
}

.alert-message p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.alert-close {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 18px;
  margin-left: auto;
  padding: 0;
}

@keyframes slideTop {
  from {
    opacity: 0;
    transform: translate(-50%, -100%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translate(100%, -50%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translate(-100%, -50%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Footer e WhatsApp (mantidos do código original) */
.site-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  text-align: left;
  color: #666;
  font-size: 0.9em;
  border-top: 1px solid #e0e0e0;
}

.whatsapp-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background-color: #25D366;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
}

.whatsapp-button i {
  color: white;
  font-size: 30px;
}

.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.whatsapp-chat {
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 300px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  overflow: hidden;
}

.chat-header {
  background-color: #075E54;
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-chat {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 18px;
}

.chat-body {
  padding: 15px;
}

.chat-body textarea {
  width: 100%;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  resize: none;
}

.chat-footer {
  padding: 10px 15px;
  text-align: right;
  background-color: #f5f5f5;
}

.send-button {
  background-color: #25D366;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
} 

.send-button:hover {
  background-color: #128C7E;
}

/* Responsividade */
@media (max-width: 768px) {
  .login-container {
    padding: 15px;
  }
  
  .login-card {
    padding: 30px 20px;
  }
  
  .logo {
    max-width: 280px;
  }
  
  .form-options {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .alert-modal {
    max-width: 90%;
  }
  
  .whatsapp-chat {
    width: 280px;
    right: 10px;
  }
  
  .whatsapp-button {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
  }
  
  .whatsapp-button i {
    font-size: 25px;
  }
}



.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px 0 30px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
}

.modal-title {
  color: #333;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 20px;
  padding: 5px;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 30px;
}

.modal-subtitle {
  color: #666;
  text-align: center;
  margin-bottom: 25px;
  font-size: 14px;
}

.modal-form {
  width: 100%;
}

.modal-actions {
  display: flex;
  gap: 15px;
  margin-top: 25px;
}

.modal-button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 38px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

.modal-button.primary {
  background-color: #E4A64E;
  color: white;
}

.modal-button.primary:hover:not(:disabled) {
  background-color: #d6963e;
}

.modal-button.secondary {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.modal-button.secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.modal-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner.small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Ajustes responsivos */
@media (max-width: 768px) {
  .modal-container {
    max-width: 90%;
    margin: 20px;
  }
  
  .modal-header {
    padding: 20px 20px 15px 20px;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}

</style>